import styled from 'styled-components';
import type { ApolloQueryResult } from '@apollo/client';
import { Waypoint } from 'react-waypoint';
import type { JSX } from 'react';
import { AnonymousCommentForm } from '../Media/Comments/Forms/AnonymousCommentForm';
import { CommentAndReplies } from '../Media/Comments/CommentsAndReplies';
import { commentingViews, useCommentContext } from '../Media/Comments/CommentContext';
import type { TopLevelComment } from '../Media/Comments/types';
import type { AnonymousMediaQuery } from './__gql/AnonymousMedia.gql.gen';
import {
  MoreAnonymousAudienceCommentsDocument,
  MoreAnonymousTeamCommentsDocument,
} from './__gql/AnonymousMedia.gql.gen';
import type { MediaPreviewQuery } from '../MediaPreview/__gql/MediaPreview.gql.gen';

const FormWrapper = styled.div`
  max-width: 70ch;
`;

const CommentsWrapper = styled.div`
  height: 100%;
`;

type CommentsProps = {
  mediaId: string;
  mediaHashedId: string;
  audienceComments:
    | NonNullable<AnonymousMediaQuery['anonymousMedia']>['topLevelAudienceComments']
    | NonNullable<MediaPreviewQuery['media']>['topLevelAudienceComments'];
  teamComments:
    | NonNullable<AnonymousMediaQuery['anonymousMedia']>['topLevelTeamComments']
    | NonNullable<MediaPreviewQuery['media']>['topLevelTeamComments'];
  commentingDisabled: boolean;
  fetchMore?: (arg: GenericRecord) => Promise<ApolloQueryResult<AnonymousMediaQuery>>;
};

export const Comments = ({
  mediaId,
  mediaHashedId,
  audienceComments,
  teamComments,
  commentingDisabled,
  fetchMore,
}: CommentsProps): JSX.Element => {
  const { commentingView } = useCommentContext();
  const audienceCommentsSelected = commentingView === commentingViews.audience;
  const topLevelComments = audienceCommentsSelected ? audienceComments : teamComments;
  const queryDocument = audienceCommentsSelected
    ? MoreAnonymousAudienceCommentsDocument
    : MoreAnonymousTeamCommentsDocument;

  const { pageInfo = {}, edges = [] } = topLevelComments as {
    pageInfo: { endCursor: string | null | undefined; hasNextPage: boolean };
    edges: { node: TopLevelComment }[];
  };
  const { endCursor, hasNextPage } = pageInfo as {
    endCursor: string | null | undefined;
    hasNextPage: boolean;
  };

  const loadMoreComments = () => {
    void fetchMore?.({
      query: queryDocument,
      variables: { cursor: endCursor, mediaHashedId },
    });
  };

  const comments = edges.map((edge: { node: TopLevelComment }) => edge.node);

  return (
    <FormWrapper>
      <AnonymousCommentForm
        commentingDisabled={commentingDisabled}
        mediaId={mediaId}
        isBelowMedia={true}
      />
      <CommentsWrapper>
        {comments.map((comment: TopLevelComment) => {
          return (
            <CommentAndReplies
              key={comment.id}
              topLevelComment={comment}
              contact={null}
              mediaId={mediaId}
              isRightOfPlayer={false}
            />
          );
        })}
        {hasNextPage ? (
          <Waypoint
            bottomOffset="-50px"
            onEnter={loadMoreComments}
          />
        ) : null}
      </CommentsWrapper>
    </FormWrapper>
  );
};
